import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon } from '@mui/icons-material';
import {
  reportService,
  MonthlyTimerLogReportEntry,
  MonthlyTimerLogReportFilters,
  MonthlyTimerLogReportPeriod,
} from '../services/reportService';
import { useAuth } from '../contexts/AuthContext';

const getPreviousMonth = (): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};

const isBreakActivity = (activityName: string): boolean =>
  activityName.toLowerCase().includes('break');

const formatSeconds = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

// Shared table style matching email colours
const headerCellSx = {
  backgroundColor: '#eef5ff',
  border: '1px solid #d6e6ff',
  fontWeight: 'bold',
  padding: '10px',
};
const bodyCellSx = { border: '1px solid #d6e6ff', padding: '8px' };
const totalCellSx = { border: '1px solid #d6e6ff', padding: '8px', backgroundColor: '#f8faff', fontWeight: 'bold' };

interface SectionTableProps {
  title: string;
  rows: MonthlyTimerLogReportEntry[];
  navigate: ReturnType<typeof useNavigate>;
}

const SectionTable: React.FC<SectionTableProps> = ({ title, rows, navigate }) => {
  if (rows.length === 0) return null;

  const totalDurationSeconds = rows.reduce((sum, r) => sum + r.duration_seconds, 0);
  const totalDeductionSeconds = rows.reduce((sum, r) => sum + r.deduction_seconds, 0);
  const totalDurationHours = Math.round(totalDurationSeconds / 36) / 100;
  const totalDeductionHours = Math.round(totalDeductionSeconds / 36) / 100;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
        {title}
      </Typography>
      <TableContainer>
        <Table size="small" sx={{ borderCollapse: 'collapse', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>User Name</TableCell>
              <TableCell sx={headerCellSx}>Case Title</TableCell>
              <TableCell sx={headerCellSx}>Notes</TableCell>
              <TableCell sx={headerCellSx}>Activity</TableCell>
              <TableCell sx={headerCellSx}>Started At</TableCell>
              <TableCell sx={headerCellSx}>Stopped At</TableCell>
              <TableCell sx={headerCellSx}>Duration (Hours)</TableCell>
              <TableCell sx={headerCellSx}>Duration (hh:mm:ss)</TableCell>
              <TableCell sx={headerCellSx}>Deduction (Hours)</TableCell>
              <TableCell sx={headerCellSx}>Deduction (hh:mm:ss)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.timer_id} hover>
                <TableCell sx={bodyCellSx}>{row.user}</TableCell>
                <TableCell sx={bodyCellSx}>
                  {row.client_firm_id ? (
                    <Button
                      size="small"
                      sx={{ p: 0, textAlign: 'left', color: '#2563eb', textDecoration: 'underline', textTransform: 'none', minWidth: 0 }}
                      onClick={() => navigate(`/firms/${row.client_firm_id}/cases/${row.case_id}`)}
                    >
                      {row.case_name}
                    </Button>
                  ) : (
                    row.case_name
                  )}
                </TableCell>
                <TableCell sx={bodyCellSx}>{row.note || ''}</TableCell>
                <TableCell sx={bodyCellSx}>{row.activity_name}</TableCell>
                <TableCell sx={bodyCellSx}>{row.start_time}</TableCell>
                <TableCell sx={bodyCellSx}>{row.end_time}</TableCell>
                <TableCell sx={bodyCellSx}>{row.duration_hours}</TableCell>
                <TableCell sx={bodyCellSx}>{row.duration_formatted}</TableCell>
                <TableCell sx={bodyCellSx}>{row.deduction_hours}</TableCell>
                <TableCell sx={bodyCellSx}>{row.deduction_formatted}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell sx={totalCellSx} colSpan={6}>Total {title}</TableCell>
              <TableCell sx={totalCellSx}>{totalDurationHours}</TableCell>
              <TableCell sx={totalCellSx}>{formatSeconds(totalDurationSeconds)}</TableCell>
              <TableCell sx={totalCellSx}>{totalDeductionHours}</TableCell>
              <TableCell sx={totalCellSx}>{formatSeconds(totalDeductionSeconds)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const MonthlyTimerLogReport: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const isAuthorized = () => {
    if (!user) return false;
    return !!(user as any).roles?.some((r: any) =>
      ['pmp_admin', 'site_admin'].includes(r.slug)
    );
  };

  useEffect(() => {
    if (user && !isAuthorized()) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const [month, setMonth] = useState<string>(getPreviousMonth());
  const [clientFirmId, setClientFirmId] = useState<number | ''>('');
  const [projectId, setProjectId] = useState<number | ''>('');
  const [userId, setUserId] = useState<number | ''>('');

  const [filters, setFilters] = useState<MonthlyTimerLogReportFilters>({
    client_firms: [],
    projects: [],
    users: [],
  });

  const [reportData, setReportData] = useState<MonthlyTimerLogReportEntry[]>([]);
  const [period, setPeriod] = useState<MonthlyTimerLogReportPeriod | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (isAuthorized()) {
      (reportService as any)
        .getMonthlyTimerLogReportFilters()
        .then((data: MonthlyTimerLogReportFilters) => setFilters(data))
        .catch(() => setError('Failed to load filter options'));
    }
  }, [user]);

  const filteredProjects = clientFirmId
    ? filters.projects.filter((p) => p.firm_id === clientFirmId)
    : filters.projects;

  const handleClientFirmChange = (value: number | '') => {
    setClientFirmId(value);
    setProjectId('');
  };

  const buildParams = () => {
    const params: any = {};
    if (month) params.month = month;
    if (clientFirmId) params.client_firm_id = clientFirmId;
    if (projectId) params.project_id = projectId;
    if (userId) params.user_id = userId;
    return params;
  };

  const handleSearch = async () => {
    setLoading(true);
    setError('');
    setSearched(false);
    try {
      const result = await (reportService as any).getMonthlyTimerLogReport(buildParams()) as any;
      setReportData(result.data);
      setPeriod(result.period);
      setSearched(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    try {
      const blob = await (reportService as any).getMonthlyTimerLogReport({
        ...buildParams(),
        export: 'csv',
      }) as Blob;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `monthly-timer-log-report_${month || 'all'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const productivityRows = reportData.filter((r) => !isBreakActivity(r.activity_name));
  const breakRows = reportData.filter((r) => isBreakActivity(r.activity_name));

  const grandTotalDurationSeconds = reportData.reduce((sum, r) => sum + r.duration_seconds, 0);
  const grandTotalDeductionSeconds = reportData.reduce((sum, r) => sum + r.deduction_seconds, 0);

  if (!isAuthorized()) {
    return (
      <Box>
        <Alert severity="error">You do not have permission to access this page.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Monthly Timer Log Report
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filter
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ minWidth: { xs: '100%', md: '180px' } }}>
            <TextField
              fullWidth
              label="Month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 7) }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
            <FormControl fullWidth>
              <InputLabel>Client</InputLabel>
              <Select
                value={clientFirmId}
                label="Client"
                onChange={(e) => handleClientFirmChange(e.target.value as number | '')}
              >
                <MenuItem value="">All Clients</MenuItem>
                {filters.client_firms.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
            <FormControl fullWidth>
              <InputLabel>Project</InputLabel>
              <Select
                value={projectId}
                label="Project"
                onChange={(e) => setProjectId(e.target.value as number | '')}
                disabled={filteredProjects.length === 0}
              >
                <MenuItem value="">All Projects</MenuItem>
                {filteredProjects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ flex: 1, minWidth: { xs: '100%', md: '200px' } }}>
            <FormControl fullWidth>
              <InputLabel>User</InputLabel>
              <Select
                value={userId}
                label="User"
                onChange={(e) => setUserId(e.target.value as number | '')}
              >
                <MenuItem value="">All Users</MenuItem>
                {filters.users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '130px' } }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
              sx={{ height: '56px' }}
            >
              Submit
            </Button>
          </Box>
          <Box sx={{ minWidth: { xs: '100%', md: '160px' } }}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              onClick={handleExport}
              disabled={exporting}
              startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              sx={{ height: '56px' }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Results — matching email layout */}
      {searched && period && (
        <Paper sx={{ p: 3 }}>
          {/* Header summary matching email highlight block */}
          <Box sx={{ backgroundColor: '#f0f4ff', border: '1px solid #d6e6ff', borderRadius: 1, p: 2, mb: 3 }}>
            <Typography variant="body2"><strong>Report Period:</strong> {period.month_name}</Typography>
            <Typography variant="body2"><strong>From:</strong> {period.start_date}</Typography>
            <Typography variant="body2"><strong>To:</strong> {period.end_date}</Typography>
            <Typography variant="body2"><strong>Total Entries:</strong> {reportData.length}</Typography>
          </Box>

          {reportData.length === 0 ? (
            <Typography>No timer entries were recorded during this period.</Typography>
          ) : (
            <>
              <SectionTable title="Productivity" rows={productivityRows} navigate={navigate} />

              {productivityRows.length > 0 && breakRows.length > 0 && (
                <Divider sx={{ my: 2 }} />
              )}

              <SectionTable title="Breaks" rows={breakRows} navigate={navigate} />

              {/* Grand total */}
              {reportData.length > 0 && (
                <Box sx={{ mt: 2, display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Grand Total Duration: {formatSeconds(grandTotalDurationSeconds)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                    Grand Total Deduction: {formatSeconds(grandTotalDeductionSeconds)}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default MonthlyTimerLogReport;
